import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, Link, MenuItem, Select, TextField, Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import moment from "moment-timezone";
import {
  GetParcelMonitoringSummary, GetParcelAlerts, GetParcelAlertStatusCounts,
  UpdateParcelAlert, RunParcelMonitoringSweep,
  UploadParcelAlertEvidence, DeleteParcelAlertEvidence,
} from "api/parcelMonitoring";
import { GetAllStationsAdmin } from "api/stations";
import { user } from "localStorage";
import { hasPageAccess } from "utils/pagePermissions";
import { useUserRefreshTick } from "hooks/useUserRefreshTick";
import TrackingDetailDialog from "components/TrackingDetialDialog.";
import PackageEditDialog from "components/PackageEditDialog";
import { GetPackageDetailByTracking } from "api/packages";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import BentoTabs from "components/bento/BentoTabs";
import EmptyState from "components/bento/EmptyState";
import KpiTile from "components/bento/KpiTile";
import StyledDataGrid from "components/bento/StyledDataGrid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import {
  TEXT_DARK, PURPLE_MAIN, PURPLE_TINT_100, BORDER,
  SUCCESS_FG, ERROR_FG, ERROR_BG, cardLabelSx,
} from "components/bento/bentoTokens";

const RULE_LABELS = {
  station_dwell: "Stuck at station",
  transit_missing: "Missing in transit",
  route_unassigned: "Not sorted (no route)",
  tracking_pending: "Tracking number pending",
};

// "" = the All tab — no status filter at all (see getAlerts/getStatusCounts
// in controllers/parcelMonitor.js). Order drives the tab bar's left-to-right order.
const STATUS_TABS = [
  { key: "", label: "All", countKey: "all" },
  { key: "open", label: "Open", countKey: "open" },
  { key: "investigating", label: "Investigating", countKey: "investigating" },
  { key: "snoozed", label: "Snoozed", countKey: "snoozed" },
  { key: "resolved", label: "Resolved", countKey: "resolved" },
];

const STATUS_COLORS = {
  open: { bg: "#fdeef0", fg: ERROR_FG },
  investigating: { bg: "#fff7e0", fg: "#9a6b00" },
  snoozed: { bg: PURPLE_TINT_100, fg: PURPLE_MAIN },
  resolved: { bg: "#e7f8ee", fg: SUCCESS_FG },
};

function StatusChip({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.open;
  return (
    <Chip
      label={status}
      size="small"
      sx={{ background: c.bg, color: c.fg, fontWeight: 700, textTransform: "capitalize" }}
    />
  );
}

const MAX_EVIDENCE_PHOTOS = 6;

// Mirrors ParcelAlert.resolution's enum (backend/models/ParcelAlert.js) and
// Package.cancelReason's enum (backend/models/Package.js) for the "cancelled"
// case — see backend/services/packageCancelService.js.
const RESOLUTION_OPTIONS = ["moved", "cancelled", "lost", "wrong_address", "false_alarm", "other", "tracking_updated"];
const RESOLUTION_LABEL_KEYS = {
  moved: "parcelMonitoring.resolutionMoved",
  cancelled: "parcelMonitoring.resolutionCancelled",
  lost: "parcelMonitoring.resolutionLost",
  wrong_address: "parcelMonitoring.resolutionWrongAddress",
  false_alarm: "parcelMonitoring.resolutionFalseAlarm",
  other: "parcelMonitoring.resolutionOther",
  tracking_updated: "parcelMonitoring.resolutionTrackingUpdated",
};
const CANCEL_REASON_OPTIONS = ["customer_request", "no_pickup", "duplicate", "undeliverable", "other"];
const CANCEL_REASON_LABEL_KEYS = {
  customer_request: "packages.cancel.reasonCustomerRequest",
  no_pickup: "packages.cancel.reasonNoPickup",
  duplicate: "packages.cancel.reasonDuplicate",
  undeliverable: "packages.cancel.reasonUndeliverable",
  other: "packages.cancel.reasonOther",
};

function ActionDialog({ alert, onClose, onSaved }) {
  const { t } = useTranslation();
  const [currentAlert, setCurrentAlert] = useState(alert);
  const [note, setNote] = useState(alert?.note || "");
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // "STD" is the placeholder pre-hub station every package starts at before
  // its first real scan — a dwell alert there means "never picked up", not
  // "stuck mid-route". Pre-select the matching cancel reason for that case.
  const neverPickedUp = currentAlert?.ruleType === "station_dwell" && currentAlert?.stationId === "STD";
  const [resolution, setResolution] = useState("");
  const [cancelReason, setCancelReason] = useState(neverPickedUp ? "no_pickup" : "");

  // Backend also enforces this (updateAlert 403s without it) — hiding the
  // option here just avoids offering an action that will fail. See
  // backend/scripts/seed-package-cancel-permissions.js.
  useUserRefreshTick(); // re-render when the cached profile refreshes — see hooks/useUserRefreshTick.js
  const canCancelPackage = hasPageAccess(user(), "packages/cancel");
  const resolutionOptions = canCancelPackage
    ? RESOLUTION_OPTIONS
    : RESOLUTION_OPTIONS.filter((r) => r !== "cancelled");

  const act = async (payload) => {
    setSaving(true);
    const res = await UpdateParcelAlert(currentAlert._id, { note, ...payload });
    setSaving(false);
    if (res.success) {
      Swal.fire({ title: "Updated!", icon: "success", timer: 1200, showConfirmButton: false });
      onSaved();
    } else {
      Swal.fire({ title: "Error", icon: "error", text: res.message });
    }
  };

  const handleResolve = () => {
    if (!resolution) {
      Swal.fire({ title: "Error", icon: "warning", text: t("parcelMonitoring.resolutionRequired") });
      return;
    }
    if (resolution === "cancelled" && !cancelReason) {
      Swal.fire({ title: "Error", icon: "warning", text: t("packages.cancel.reasonRequired") });
      return;
    }
    if (resolution === "other" && !note.trim()) {
      Swal.fire({ title: "Error", icon: "warning", text: t("parcelMonitoring.otherNoteRequired") });
      return;
    }
    act({
      status: "resolved",
      resolution,
      ...(resolution === "cancelled" ? { cancelReason } : {}),
    });
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file later
    if (files.length === 0) return;
    const remaining = MAX_EVIDENCE_PHOTOS - (currentAlert.evidencePhotos?.length || 0);
    const toUpload = files.slice(0, Math.max(remaining, 0));
    setUploadingPhoto(true);
    for (const file of toUpload) {
      const res = await UploadParcelAlertEvidence(currentAlert._id, file);
      if (res.success) setCurrentAlert(res.data);
    }
    setUploadingPhoto(false);
  };

  const handleRemovePhoto = async (photoPath) => {
    const filename = photoPath.split("/").pop();
    const res = await DeleteParcelAlertEvidence(currentAlert._id, filename);
    if (res.success) setCurrentAlert(res.data);
  };

  if (!currentAlert) return null;

  const photos = currentAlert.evidencePhotos || [];

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {currentAlert.tracking} — {RULE_LABELS[currentAlert.ruleType] || currentAlert.ruleType}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: neverPickedUp ? 1 : 2 }}>
          Station: {currentAlert.stationId}
          {currentAlert.toStationId ? ` → ${currentAlert.toStationId}` : ""} · {currentAlert.daysStuck} day(s) (threshold {currentAlert.thresholdDays})
        </Typography>
        {neverPickedUp && (
          <Chip
            label={t("parcelMonitoring.neverPickedUpBadge")}
            size="small"
            sx={{ background: ERROR_BG, color: ERROR_FG, fontWeight: 700, mb: 2 }}
          />
        )}
        <TextField
          fullWidth multiline rows={3} label="Note" value={note}
          onChange={(e) => setNote(e.target.value)} size="small"
          required={resolution === "other"}
          helperText={resolution === "other" ? t("parcelMonitoring.otherNoteRequired") : ""}
          sx={{ mb: 2 }}
        />

        <FormControl size="small" fullWidth sx={{ mb: resolution === "cancelled" ? 2 : 0 }}>
          <InputLabel>{t("parcelMonitoring.resolutionLabel")}</InputLabel>
          <Select
            label={t("parcelMonitoring.resolutionLabel")}
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
          >
            {resolutionOptions.map((r) => (
              <MenuItem key={r} value={r}>{t(RESOLUTION_LABEL_KEYS[r])}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {resolution === "cancelled" && (
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("packages.cancel.reasonLabel")}</InputLabel>
            <Select
              label={t("packages.cancel.reasonLabel")}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            >
              {CANCEL_REASON_OPTIONS.map((r) => (
                <MenuItem key={r} value={r}>{t(CANCEL_REASON_LABEL_KEYS[r])}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Typography sx={{ ...cardLabelSx, mb: 1 }}>
          Evidence photos ({photos.length}/{MAX_EVIDENCE_PHOTOS})
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {photos.map((photo) => (
            <Box
              key={photo.path}
              sx={{
                position: "relative", width: 72, height: 72, borderRadius: "10px",
                overflow: "hidden", border: `1px solid ${BORDER}`,
              }}
            >
              <Box component="img" src={photo.path} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <IconButton
                size="small"
                onClick={() => handleRemovePhoto(photo.path)}
                sx={{
                  position: "absolute", top: 2, right: 2, width: 20, height: 20,
                  background: "rgba(0,0,0,0.55)", color: "#fff",
                  "&:hover": { background: "rgba(0,0,0,0.75)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          ))}
          {photos.length < MAX_EVIDENCE_PHOTOS && (
            <Box
              onClick={() => !uploadingPhoto && photoInputRef.current?.click()}
              sx={{
                width: 72, height: 72, borderRadius: "10px", border: `1.5px dashed ${BORDER}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: uploadingPhoto ? "default" : "pointer",
              }}
            >
              {uploadingPhoto ? <CircularProgress size={18} color="secondary" /> : <AddPhotoAlternateIcon color="disabled" />}
            </Box>
          )}
        </Box>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFilesSelected}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button variant="outlined" color="secondary" disabled={saving} onClick={() => act({ status: "investigating" })}>
          Investigating
        </Button>
        {currentAlert.ruleType === "tracking_pending" ? (
          <>
            <Button variant="outlined" color="secondary" disabled={saving} onClick={() => act({ status: "snoozed", snoozeDays: 1 })}>
              Snooze 1d
            </Button>
            <Button variant="outlined" color="secondary" disabled={saving} onClick={() => act({ status: "snoozed", snoozeDays: 2 })}>
              Snooze 2d
            </Button>
            <Button variant="outlined" color="secondary" disabled={saving} onClick={() => act({ status: "snoozed", snoozeDays: 3 })}>
              Snooze 3d
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" color="secondary" disabled={saving} onClick={() => act({ status: "snoozed", snoozeDays: 3 })}>
              Snooze 3d
            </Button>
            <Button variant="outlined" color="secondary" disabled={saving} onClick={() => act({ status: "snoozed", snoozeDays: 7 })}>
              Snooze 7d
            </Button>
          </>
        )}
        <Button
          variant="contained" color="secondary" disabled={saving}
          onClick={handleResolve}
        >
          {resolution === "cancelled" ? t("parcelMonitoring.cancelPackageAction") : "Resolve"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const ParcelMonitoring = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [statusCounts, setStatusCounts] = useState({ all: 0, open: 0, investigating: 0, snoozed: 0, resolved: 0 });
  const [stations, setStations] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [stationFilter, setStationFilter] = useState("");
  const [ruleFilter, setRuleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [severityFilter, setSeverityFilter] = useState("");

  const [activeAlert, setActiveAlert] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState(null);
  const [editPackage, setEditPackage] = useState(null);

  // Non-admins get a change request instead of a direct save — that rule
  // lives in PackageEditDialog itself, same as on the Packages page.
  const handleEditClick = async (row) => {
    const res = await GetPackageDetailByTracking(row.tracking);
    if (res.status === 200 && res.data) {
      setEditPackage(res.data);
    } else {
      Swal.fire({ title: "Error", icon: "error", text: res.message || res.data?.message || t("parcelMonitoring.packageNotFound") });
    }
  };

  const loadSummary = useCallback(async () => {
    const res = await GetParcelMonitoringSummary();
    if (res.success) setSummary(res.data);
  }, []);

  const loadStatusCounts = useCallback(async () => {
    const res = await GetParcelAlertStatusCounts({
      station: stationFilter, ruleType: ruleFilter, severity: severityFilter, search,
    });
    if (res.success) setStatusCounts(res.data);
  }, [stationFilter, ruleFilter, severityFilter, search]);

  const loadAlerts = useCallback(async () => {
    setIsLoading(true);
    const res = await GetParcelAlerts(page, pageSize, sort, search, {
      station: stationFilter, ruleType: ruleFilter, status: statusFilter, severity: severityFilter,
    });
    setRows(res.data || []);
    setTotalCount(res.total || 0);
    setIsLoading(false);
  }, [page, pageSize, sort, search, stationFilter, ruleFilter, statusFilter, severityFilter]);

  useEffect(() => {
    GetAllStationsAdmin().then((res) => {
      if (res.success) setStations(res.data || []);
    });
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadStatusCounts();
  }, [loadStatusCounts]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleRunNow = async () => {
    setRunning(true);
    const res = await RunParcelMonitoringSweep();
    setRunning(false);
    if (res.success) {
      Swal.fire({
        title: "Sweep complete",
        icon: "success",
        text: `${res.data.violations} active, ${res.data.newlyOpened} new, ${res.data.resolved} resolved`,
        timer: 2500,
      });
      loadSummary();
      loadStatusCounts();
      loadAlerts();
    } else {
      Swal.fire({ title: "Error", icon: "error", text: res.message });
    }
  };

  const columns = [
    {
      field: "tracking", headerName: "Tracking", flex: 1.2,
      renderCell: (params) => (
        <Link
          component="button"
          color="secondary"
          underline="hover"
          onClick={() => setTrackingNumber(params.row.tracking)}
        >
          {params.row.tracking}
        </Link>
      ),
    },
    {
      field: "ruleType", headerName: "Issue", flex: 1.3,
      valueGetter: (params) => RULE_LABELS[params.row.ruleType] || params.row.ruleType,
    },
    { field: "stationId", headerName: "Station", flex: 0.8 },
    {
      field: "daysStuck", headerName: "Days", flex: 0.6,
      renderCell: (params) => (
        <span style={{ fontWeight: 700, color: params.row.severity === "critical" ? ERROR_FG : TEXT_DARK }}>
          {params.value}
        </span>
      ),
    },
    {
      field: "severity", headerName: "Severity", flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value} size="small"
          sx={params.value === "critical" ? { background: ERROR_BG, color: ERROR_FG, fontWeight: 700 } : {}}
        />
      ),
    },
    {
      field: "status", headerName: "Status", flex: 0.8,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "updatedAt", headerName: "Last seen", flex: 1,
      valueGetter: (params) => moment(params.row.lastSeenAt).tz("Asia/Vientiane").format("DD/MM/YYYY HH:mm"),
    },
    {
      field: "actions", headerName: "", flex: 1, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" color="secondary" size="small" onClick={() => handleEditClick(params.row)}>
            {t("parcelMonitoring.editPackage")}
          </Button>
          <Button variant="outlined" color="secondary" size="small" onClick={() => setActiveAlert(params.row)}>
            Manage
          </Button>
        </Box>
      ),
    },
  ];

  const kpi = summary || { total: 0, critical: 0, byRule: {}, byStation: [] };

  return (
    <PageShell
      title={t("parcelMonitoring.title", "Parcel Monitoring")}
      subtitle={t("parcelMonitoring.subtitle", "Stuck, lost, and unsorted parcels that need attention")}
      actions={
        <Button variant="contained" color="secondary" disabled={running} onClick={handleRunNow}>
          {running ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Run now
        </Button>
      }
    >
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: "20px", mb: "24px" }}>
        <KpiTile label="Open alerts" value={kpi.total} onClick={() => setStatusFilter("")} />
        <KpiTile label="Critical" value={kpi.critical} valueColor={ERROR_FG} />
        <KpiTile
          label="Stuck at station" value={kpi.byRule.station_dwell || 0}
          onClick={() => setRuleFilter("station_dwell")}
        />
        <KpiTile
          label="Missing in transit" value={kpi.byRule.transit_missing || 0}
          onClick={() => setRuleFilter("transit_missing")}
        />
      </Box>

      <BentoCard title="By station" sx={{ mb: "24px" }}>
        {kpi.byStation.length === 0 ? (
          <EmptyState message="No active alerts." />
        ) : (
          <Box sx={{ p: "18px 24px" }}>
            {kpi.byStation.map((s, i) => (
              <Box
                key={s.stationId}
                onClick={() => setStationFilter(s.stationId)}
                sx={{
                  display: "flex", alignItems: "center", gap: "14px", py: "10px", cursor: "pointer",
                  borderBottom: i < kpi.byStation.length - 1 ? "1px solid #f1eff5" : "none",
                }}
              >
                <Box sx={{ ...cardLabelSx, width: 28 }}>{String(i + 1).padStart(2, "0")}</Box>
                <Box sx={{ flex: 1, fontWeight: 600, color: TEXT_DARK }}>{s.stationId}</Box>
                {s.critical > 0 && (
                  <Chip label={`${s.critical} critical`} size="small" sx={{ background: ERROR_BG, color: ERROR_FG }} />
                )}
                <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: TEXT_DARK, minWidth: 30, textAlign: "right" }}>
                  {s.count}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </BentoCard>

      <Box sx={{ mb: "16px" }}>
        <BentoTabs
          tabs={STATUS_TABS.map((t) => ({ key: t.key, label: t.label, badge: statusCounts[t.countKey] || 0 }))}
          value={STATUS_TABS.findIndex((t) => t.key === statusFilter)}
          onChange={(idx) => setStatusFilter(STATUS_TABS[idx].key)}
        />
      </Box>

      <BentoCard sx={{ mb: "16px" }} bodySx={{ p: "18px 24px" }}>
        <Box
          sx={{
            display: "grid", gap: "14px",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "repeat(4, 1fr)" },
          }}
        >
          <FormControl size="small">
            <InputLabel>Station</InputLabel>
            <Select label="Station" value={stationFilter} onChange={(e) => setStationFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {stations.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Issue</InputLabel>
            <Select label="Issue" value={ruleFilter} onChange={(e) => setRuleFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(RULE_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Severity</InputLabel>
            <Select label="Severity" value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined" color="secondary"
            onClick={() => { setStationFilter(""); setRuleFilter(""); setStatusFilter(""); setSeverityFilter(""); }}
          >
            Clear filters
          </Button>
        </Box>
      </BentoCard>

      <Box height="65vh">
        <StyledDataGrid
          loading={isLoading}
          getRowId={(row) => row._id}
          rows={rows || []}
          columns={columns}
          rowCount={totalCount || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: { searchInput: search, setSearchInput: setSearch, setSearch },
          }}
          containerSx={{ height: "100%" }}
          autoHeight={false}
        />
      </Box>

      {activeAlert && (
        <ActionDialog
          alert={activeAlert}
          onClose={() => setActiveAlert(null)}
          onSaved={() => { setActiveAlert(null); loadSummary(); loadStatusCounts(); loadAlerts(); }}
        />
      )}

      <PackageEditDialog
        open={!!editPackage}
        onClose={() => setEditPackage(null)}
        packageData={editPackage}
        onSuccess={() => {
          setEditPackage(null);
          loadAlerts();
        }}
      />

      <TrackingDetailDialog
        open={!!trackingNumber}
        onClose={() => setTrackingNumber(null)}
        trackingNumber={trackingNumber}
      />
    </PageShell>
  );
};

export default ParcelMonitoring;
